/**
 * Workflow Template Validation State Manager
 * 
 * Centralized state management for workflow template validation.
 * This is the single source of truth for all validation state across tabs.
 * 
 * Features:
 * - Tracks validity, dirty state, and mid-edit state for all 5 tabs
 * - Tracks variable definitions and usage across tabs
 * - Computes overall validity and canSave state
 * - Handles cross-tab dependencies (Transfer Type → Scheduler requirement)
 * - Provides navigation warning state
 * 
 * Usage:
 * 1. Create instance in WorkflowTemplateGeneralView
 * 2. Each tab calls updateTabState() when validation changes
 * 3. Parent component subscribes to state changes via onStateChange callback
 * 4. Save button and tab icons read from this manager
 * 
 * @see docs/workflow-template-validation-plan.md for full requirements
 */

import {
    TabValidationState,
    TabName,
    TransferType,
    TemplateMode,
    WorkflowTemplateValidationState,
    ValidationChangeEventDetail,
    createInitialValidationState,
} from '../types/WorkflowTemplates/WorkflowTemplateValidationState';
import {
    extractTemplateVariableNames,
    hasTemplateVariablePlaceholders,
    getUndefinedTemplateVariables,
    allTemplateVariablesDefined as allTemplateVariablesDefinedInString,
    isBuiltinRegionVariableName,
} from '../utils/workflowTemplatePlaceholderSyntax';

/**
 * Callback type for state change notifications
 */
export type StateChangeCallback = (state: WorkflowTemplateValidationState) => void;

/**
 * Centralized validation state manager for workflow templates
 */
export class WorkflowTemplateValidationStateManager {
    private state: WorkflowTemplateValidationState;
    private onStateChange: StateChangeCallback | null = null;
    private saveInProgress: boolean = false;
    private apiLoading: boolean = false;

    constructor(mode: TemplateMode = 'ADD') {
        this.state = createInitialValidationState(mode);
    }

    // ===== State Access =====

    /**
     * Get the current validation state (read-only snapshot)
     */
    getState(): Readonly<WorkflowTemplateValidationState> {
        return this.state;
    }

    /**
     * Get the current mode
     */
    getMode(): TemplateMode {
        return this.state.mode;
    }

    /**
     * Get the transfer type
     */
    getTransferType(): TransferType {
        return this.state.transferType;
    }

    /**
     * Get defined variables list
     */
    getDefinedVariables(): string[] {
        return [...this.state.definedVariables];
    }

    /**
     * Check if a variable is defined
     */
    isVariableDefined(variableName: string): boolean {
        return this.state.definedVariables.includes(variableName);
    }

    /**
     * Check if a variable is in use (cannot be deleted)
     */
    isVariableInUse(variableName: string): boolean {
        const usage = this.state.variableUsage.get(variableName);
        return usage !== undefined && usage.length > 0;
    }

    /**
     * Get locations where a variable is used
     */
    getVariableUsageLocations(variableName: string): string[] {
        return this.state.variableUsage.get(variableName) || [];
    }

    // ===== Computed State =====

    /**
     * Whether all tabs are valid (for enabling Save button)
     */
    get allValid(): boolean {
        return this.state.allValid;
    }

    /**
     * Whether any tab has unsaved changes
     */
    get anyDirty(): boolean {
        return this.state.anyDirty;
    }

    /**
     * Whether any tab has a mid-edit state
     */
    get anyMidEdit(): boolean {
        return this.state.anyMidEdit;
    }

    /**
     * Whether the Save button should be enabled
     */
    get canSave(): boolean {
        return this.state.canSave && !this.saveInProgress && !this.apiLoading;
    }

    /**
     * Whether there are unsaved changes (for navigation warning)
     */
    get hasUnsavedChanges(): boolean {
        return this.state.hasUnsavedChanges;
    }

    // ===== State Updates =====

    /**
     * Subscribe to state changes
     */
    subscribe(callback: StateChangeCallback): void {
        this.onStateChange = callback;
    }

    /**
     * Unsubscribe from state changes
     */
    unsubscribe(): void {
        this.onStateChange = null;
    }

    /**
     * Set the template mode
     */
    setMode(mode: TemplateMode): void {
        if (this.state.mode !== mode) {
            this.state.mode = mode;
            this.notifyStateChange();
        }
    }

    /**
     * Update a tab's validation state
     */
    updateTabState(tabName: TabName, update: Partial<TabValidationState>): void {
        const currentTab = this.state.tabs[tabName];
        const newTab = { ...currentTab, ...update };
        
        // Only update and notify if something actually changed
        if (currentTab.valid !== newTab.valid ||
            currentTab.dirty !== newTab.dirty ||
            currentTab.midEdit !== newTab.midEdit) {
            this.state.tabs[tabName] = newTab;
            this.notifyStateChange();
        }
    }

    /**
     * Handle validation-change event from a tab component
     */
    handleValidationChange(detail: ValidationChangeEventDetail): void {
        const { tabName, valid, dirty, midEdit, transferType, variables } = detail;

        // Update tab state
        const update: Partial<TabValidationState> = { valid };
        if (dirty !== undefined) update.dirty = dirty;
        if (midEdit !== undefined) update.midEdit = midEdit;
        
        this.updateTabState(tabName, update);

        // Handle transfer type change (from workflowInfo tab)
        if (tabName === 'workflowInfo' && transferType !== undefined) {
            this.setTransferType(transferType);
        }

        // Handle variables update (from settings tab)
        if (tabName === 'settings' && variables !== undefined) {
            this.setDefinedVariables(variables.map(v => v.name));
        }
    }

    /**
     * Set the transfer type (affects scheduler requirement)
     */
    setTransferType(transferType: TransferType): void {
        if (this.state.transferType !== transferType) {
            this.state.transferType = transferType;
            this.notifyStateChange();
        }
    }

    /**
     * Set the list of defined variables
     */
    setDefinedVariables(variableNames: string[]): void {
        this.state.definedVariables = [...variableNames];
        this.checkForUndefinedVariables();
        this.notifyStateChange();
    }

    /**
     * Register variable usage in a specific location
     */
    registerVariableUsage(variableName: string, location: string): void {
        const usage = this.state.variableUsage.get(variableName) || [];
        if (!usage.includes(location)) {
            usage.push(location);
            this.state.variableUsage.set(variableName, usage);
            this.checkForUndefinedVariables();
            this.notifyStateChange();
        }
    }

    /**
     * Unregister variable usage from a specific location
     */
    unregisterVariableUsage(variableName: string, location: string): void {
        const usage = this.state.variableUsage.get(variableName);
        if (usage) {
            const index = usage.indexOf(location);
            if (index > -1) {
                usage.splice(index, 1);
                if (usage.length === 0) {
                    this.state.variableUsage.delete(variableName);
                } else {
                    this.state.variableUsage.set(variableName, usage);
                }
                this.checkForUndefinedVariables();
                this.notifyStateChange();
            }
        }
    }

    /**
     * Clear all variable usage for a specific location (e.g., when a tab clears its data)
     */
    clearVariableUsageForLocation(locationPrefix: string): void {
        let changed = false;
        for (const [varName, locations] of this.state.variableUsage.entries()) {
            const filtered = locations.filter(loc => !loc.startsWith(locationPrefix));
            if (filtered.length !== locations.length) {
                if (filtered.length === 0) {
                    this.state.variableUsage.delete(varName);
                } else {
                    this.state.variableUsage.set(varName, filtered);
                }
                changed = true;
            }
        }
        if (changed) {
            this.checkForUndefinedVariables();
            this.notifyStateChange();
        }
    }

    /**
     * Update variable usage for a field (replaces all usage for that field)
     */
    updateVariableUsageForField(fieldLocation: string, variableNames: string[]): void {
        // Clear existing usage for this field
        for (const [varName, locations] of this.state.variableUsage.entries()) {
            const filtered = locations.filter(loc => loc !== fieldLocation);
            if (filtered.length === 0) {
                this.state.variableUsage.delete(varName);
            } else if (filtered.length !== locations.length) {
                this.state.variableUsage.set(varName, filtered);
            }
        }

        // Add new usage
        for (const varName of variableNames) {
            const usage = this.state.variableUsage.get(varName) || [];
            if (!usage.includes(fieldLocation)) {
                usage.push(fieldLocation);
                this.state.variableUsage.set(varName, usage);
            }
        }

        this.checkForUndefinedVariables();
        this.notifyStateChange();
    }

    // ===== Loading States =====

    /**
     * Set save in progress state
     */
    setSaveInProgress(inProgress: boolean): void {
        if (this.saveInProgress !== inProgress) {
            this.saveInProgress = inProgress;
            this.notifyStateChange();
        }
    }

    /**
     * Set API loading state
     */
    setApiLoading(loading: boolean): void {
        if (this.apiLoading !== loading) {
            this.apiLoading = loading;
            this.notifyStateChange();
        }
    }

    // ===== Reset =====

    /**
     * Reset all dirty states (after successful save)
     */
    resetDirtyStates(): void {
        for (const tabName of Object.keys(this.state.tabs) as TabName[]) {
            this.state.tabs[tabName].dirty = false;
        }
        this.notifyStateChange();
    }

    /**
     * Reset to initial state
     */
    reset(mode: TemplateMode = 'ADD'): void {
        this.state = createInitialValidationState(mode);
        this.saveInProgress = false;
        this.apiLoading = false;
        this.notifyStateChange();
    }

    /**
     * Initialize for editing an existing template
     */
    initializeForEdit(): void {
        this.state.mode = 'EDIT';
        // When loading existing data, all tabs should be valid initially
        // They'll report their actual state as they load
        this.notifyStateChange();
    }

    // ===== Tab Icon State =====

    /**
     * Get the icon state for a tab (for rendering tab icons)
     */
    getTabIconState(tabName: TabName): 'valid' | 'warning' | 'view' | 'none' {
        if (this.state.mode === 'VIEW') {
            return 'view';
        }

        const tab = this.state.tabs[tabName];
        
        // Check for undefined variables in this tab
        const hasUndefinedVars = this.hasUndefinedVariablesInTab(tabName);
        
        if (!tab.valid || tab.midEdit || hasUndefinedVars) {
            return 'warning';
        }

        return 'valid';
    }

    /**
     * Check if a specific tab has undefined variable references
     */
    private hasUndefinedVariablesInTab(tabName: TabName): boolean {
        for (const [varName, locations] of this.state.variableUsage.entries()) {
            if (isBuiltinRegionVariableName(varName)) continue;
            if (!this.state.definedVariables.includes(varName)) {
                if (locations.some(loc => loc.startsWith(tabName))) {
                    return true;
                }
            }
        }
        return false;
    }

    // ===== Private Methods =====

    /**
     * Check for any undefined variable references
     */
    private checkForUndefinedVariables(): void {
        let hasUndefined = false;
        for (const varName of this.state.variableUsage.keys()) {
            if (isBuiltinRegionVariableName(varName)) continue;
            if (!this.state.definedVariables.includes(varName)) {
                hasUndefined = true;
                break;
            }
        }
        this.state.hasUndefinedVariables = hasUndefined;
    }

    /**
     * Notify subscriber of state change
     */
    private notifyStateChange(): void {
        if (this.onStateChange) {
            this.onStateChange(this.state);
        }
    }

    // ===== Debug =====

    /**
     * Get debug info about current state
     */
    getDebugInfo(): object {
        return {
            mode: this.state.mode,
            transferType: this.state.transferType,
            tabs: { ...this.state.tabs },
            definedVariables: [...this.state.definedVariables],
            variableUsage: Object.fromEntries(this.state.variableUsage),
            hasUndefinedVariables: this.state.hasUndefinedVariables,
            computed: {
                allValid: this.state.allValid,
                anyDirty: this.state.anyDirty,
                anyMidEdit: this.state.anyMidEdit,
                canSave: this.canSave,
                hasUnsavedChanges: this.state.hasUnsavedChanges,
            },
            loadingStates: {
                saveInProgress: this.saveInProgress,
                apiLoading: this.apiLoading,
            },
        };
    }
}

/**
 * Extract template variable names from a string ({variableName} syntax).
 */
export function extractVariableNames(value: string): string[] {
    return extractTemplateVariableNames(value);
}

/**
 * Check if a value contains any {variableName} template placeholders
 */
export function hasVariableReferences(value: string): boolean {
    return hasTemplateVariablePlaceholders(value);
}

/**
 * Check if all {variableName} references in a value are defined
 */
export function allVariablesDefined(value: string, definedVariables: string[]): boolean {
    return allTemplateVariablesDefinedInString(value, definedVariables);
}

/**
 * Get undefined template variable names from a value
 */
export function getUndefinedVariables(value: string, definedVariables: string[]): string[] {
    return getUndefinedTemplateVariables(value, definedVariables);
}

export {
    extractTemplateVariableNames,
    hasTemplateVariablePlaceholders,
    getUndefinedTemplateVariables,
    findDeprecatedLegacyBracketTemplateNames,
    hasDeprecatedLegacyBracketTemplateSyntax,
    stripTemplateVariablePlaceholders,
    isKnownFileRenameToken,
} from '../utils/workflowTemplatePlaceholderSyntax';
